/**
 * A list of rows, divided by hairlines. Use <ListItem> for each row.
 * `variant`: "plain" (hairlines only, default) | "card" (rows inside a bordered surface).
 * `as`: "ul" (default) | "ol" | "div". `label` names the list for screen readers when there is no heading.
 * (cockatrice-ui `src/ui/List.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/list.css';
import { mergeProps, splitProps } from 'solid-js';
import { dynamic, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface ListProps extends Attributes {
  /** "plain" hairlines or "card" inside a bordered surface. Default "plain". */
  readonly variant?: 'plain' | 'card' | undefined;
  /** Element. Default "ul". */
  readonly as?: 'ul' | 'ol' | 'div' | undefined;
  /** Accessible name when there is no heading. */
  readonly label?: string | undefined;
  /** Tighter rows. */
  readonly dense?: boolean | undefined;
  /** ListItem elements. */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}
export interface ListItemProps extends Attributes {
  /** Main text. */
  readonly title: Child;
  /** Caption line under the title. */
  readonly meta?: Child;
  /** Longer text that wraps. */
  readonly description?: Child;
  /** Avatar, cover or icon. */
  readonly leading?: Child;
  /** Actions or badge; separate tab stops. */
  readonly trailing?: Child;
  /** Makes the row a link. */
  readonly href?: string | undefined;
  /** Makes the row a button. */
  readonly onClick?: ((e: MouseEvent) => void) | undefined;
  /** The item you are on (aria-current). */
  readonly current?: boolean | undefined;
  /** Chosen item: ink-blue bar plus hidden text. */
  readonly selected?: boolean | undefined;
  /** Hidden text appended when selected. Default "selected"; false for none. */
  readonly selectedLabel?: string | false | undefined;
  /** Disables the button form. */
  readonly disabled?: boolean | undefined;
  /** Element. Default "li" ("div" inside a div list). */
  readonly as?: 'li' | 'div' | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
  /** Extra content after the row. */
  readonly children?: Child;
}

export default function List(props: ListProps): Child {
  const [local, rest] = splitProps(props, ['class', 'variant', 'as', 'label', 'children', 'dense']);
  return dynamic(
    () => local.as ?? 'ul',
    mergeProps({
      class: () => ['ui-list', `ui-list--${local.variant ?? 'plain'}`, local.dense === true && 'ui-list--dense', local.class].filter(Boolean).join(' '),
      'aria-label': () => local.label,
      role: () => (local.as === 'div' ? 'list' : undefined),
    }, rest),
    () => local.children,
  );
}

/**
 * One row: leading (avatar, cover, icon) · title + meta · trailing (actions, badge).
 * `href` makes the main area a link; `onClick` makes it a button (actions stay separate controls).
 * `current` marks the item you are on (aria-current), `selected` marks a chosen one (aria-selected is
 * only for listbox roles, so this sets data-selected for style plus the `selectedLabel` for screen readers).
 */
export function ListItem(props: ListItemProps): Child {
  const [local, rest] = splitProps(props, ['class', 'leading', 'title', 'meta', 'description', 'trailing', 'href', 'onClick', 'current', 'selected', 'selectedLabel', 'children', 'disabled', 'as']);
  const linked = (): boolean => local.href !== undefined && local.href !== '';
  const interactive = (): boolean => linked() || local.onClick !== undefined;
  const main = (): Child => [
    when(
      () => Boolean(local.leading),
      () => el('span', { class: 'ui-list-item__leading' }, () => local.leading),
    ),
    el(
      'span',
      { class: 'ui-list-item__text' },
      el('span', { class: 'ui-list-item__title' }, () => local.title),
      when(
        () => Boolean(local.meta),
        () => el('span', { class: 'ui-list-item__meta' }, () => local.meta),
      ),
      when(
        () => Boolean(local.description),
        () => el('span', { class: 'ui-list-item__description' }, () => local.description),
      ),
      when(
        () => local.selected === true && local.selectedLabel !== false,
        () => el('span', { class: 'sr-only' }, ', ', () => (typeof local.selectedLabel === 'string' ? local.selectedLabel : uiText()('ui.list.selected'))),
      ),
    ),
  ];
  return dynamic(
    () => local.as ?? 'li',
    mergeProps({
      role: () => (local.as === 'div' ? 'listitem' : undefined),
      class: () => ['ui-list-item', interactive() && 'ui-list-item--interactive', local.class].filter(Boolean).join(' '),
      'data-selected': () => (local.selected === true ? 'true' : undefined),
      'data-current': () => (local.current === true ? 'true' : undefined),
    }, rest),
    when(
      interactive,
      () =>
        dynamic(
          () => (linked() ? 'a' : 'button'),
          {
            type: () => (linked() ? undefined : 'button'),
            href: () => local.href,
            class: 'ui-list-item__main',
            'aria-current': () => (local.current === true ? 'true' : undefined),
            disabled: () => !linked() && local.disabled === true,
            onClick: (e: MouseEvent) => {
              local.onClick?.(e);
            },
          },
          main(),
        ),
      () => el('div', { class: 'ui-list-item__main' }, main()),
    ),
    when(
      () => Boolean(local.trailing),
      () => el('div', { class: 'ui-list-item__trailing' }, () => local.trailing),
    ),
    () => local.children,
  );
}
